"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { supabase } from "@/lib/supabase";
import { BUILTIN_TOOL_UUID } from "@/lib/builtin-tools";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  sumChatMessageMetadata,
  type ChatMessageMetadata,
  type ChatUIMessage,
} from "@/types/chat";
import type { ActiveToolConfig } from "@/types/settings";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MessageResponse } from "@/components/ai-elements/message";
import { toast } from "sonner";
import { BrainIcon, CopyIcon, SendIcon, SquareIcon, WrenchIcon, XIcon } from "lucide-react";

// ─── Tool config ──────────────────────────────────────────────────────────────

type ToolFlags = {
  webSearch: boolean;
  webFetch: boolean;
  codeExecution: boolean;
  memory: boolean;
  knowledgeBase: boolean;
};

const DEFAULT_TOOLS: ToolFlags = {
  webSearch: true,
  webFetch: true,
  codeExecution: true,
  memory: true,
  knowledgeBase: false,
};

function buildActiveTools(
  flags: ToolFlags,
  kbFileIds: string[]
): ActiveToolConfig[] {
  const tools: ActiveToolConfig[] = [];
  if (flags.webSearch) {
    tools.push({
      toolId: "webSearch",
      executorConfig: {
        tool_id: "webSearch",
        tool_name: "web_search",
        providers: {
          anthropic: { feature_key: "web_search_20260209", options: { max_uses: 10 } },
        },
      },
    });
  }
  if (flags.webFetch) {
    tools.push({
      toolId: "webFetch",
      executorConfig: {
        provider: "anthropic",
        feature_key: "web_fetch_20260209",
        tool_name: "web_fetch",
        tool_id: "webFetch",
        options: { max_uses: 10 },
      },
    });
  }
  if (flags.codeExecution) {
    tools.push({
      toolId: "codeExecution",
      executorConfig: {
        tool_id: "codeExecution",
        tool_name: "code_execution",
        providers: {
          anthropic: { feature_key: "code_execution_20260120" },
        },
      },
    });
  }
  if (flags.memory) {
    tools.push({
      toolId: "memory",
      executorConfig: {
        provider: "anthropic",
        feature_key: "memory_20250818",
        tool_name: "memory",
        tool_id: "memory",
      },
    });
  }
  if (flags.knowledgeBase && kbFileIds.length > 0) {
    tools.push({
      toolId: "knowledgeBase",
      executorConfig: {
        provider: "internal",
        feature_key: "knowledge_base",
        tool_name: "knowledge_base",
        tool_id: "knowledgeBase",
      },
      config: { fileIds: kbFileIds },
    });
  }
  return tools;
}

// ─── Model config & pricing ───────────────────────────────────────────────────

type ModelOption = {
  id: string;
  label: string;
  /** USD per million tokens */
  price: { input: number; cacheWrite: number; cacheRead: number; output: number };
};

const MODEL_OPTIONS: ModelOption[] = [
  {
    id: "claude-sonnet-5-5",
    label: "Sonnet 5.5",
    price: { input: 2.0, cacheWrite: 2.50, cacheRead: 0.20, output: 10.0 },
  },
  {
    id: "claude-opus-5-5",
    label: "Opus 5.5",
    price: { input: 4.0, cacheWrite: 5.00, cacheRead: 0.20, output: 20.0 },
  },
  {
    id: "claude-haiku-4-5",
    label: "Haiku 4.5",
    price: { input: 0.80, cacheWrite: 1.00, cacheRead: 0.08, output: 4.0 },
  },
];

const DEFAULT_MODEL = MODEL_OPTIONS[0];

/** Approximate NZD rate — not live */
const USD_TO_NZD = 1.65;

function calcCostUsd(meta: ChatMessageMetadata, price: ModelOption["price"]): number {
  const netIn = Math.max(0, (meta.inputTokens ?? 0) - (meta.cacheReadTokens ?? 0));
  return (
    (netIn                       * price.input      +
     (meta.cacheReadTokens  ?? 0) * price.cacheRead  +
     (meta.cacheWriteTokens ?? 0) * price.cacheWrite +
     (meta.outputTokens     ?? 0) * price.output)
    / 1_000_000
  );
}

function fmtUsd(usd: number): string {
  const precision = usd < 0.01 ? 6 : 4;
  return `$${usd.toFixed(precision)}`;
}

function fmtNzd(usd: number): string {
  const nzd = usd * USD_TO_NZD;
  const precision = nzd < 0.01 ? 6 : 4;
  return `NZ$${nzd.toFixed(precision)}`;
}

// ─── Token math ───────────────────────────────────────────────────────────────

function netInput(m: ChatMessageMetadata): number {
  return Math.max(0, (m.inputTokens ?? 0) - (m.cacheReadTokens ?? 0));
}

function pct(numerator: number, denominator: number): string {
  if (denominator === 0) return "—";
  return `${Math.round((numerator / denominator) * 100)}%`;
}

// ─── Expert report ────────────────────────────────────────────────────────────

function buildReport(
  messages: ChatUIMessage[],
  overall: ChatMessageMetadata | undefined,
  tools: ToolFlags,
  kbFileIds: string[],
  model: ModelOption
): string {
  const turns = messages
    .filter((m) => m.role === "assistant")
    .map((m, i) => {
      const meta = m.metadata as ChatMessageMetadata | undefined;
      return {
        turn: i + 1,
        inputTokens: meta?.inputTokens ?? 0,
        cacheRead: meta?.cacheReadTokens ?? 0,
        cacheWrite: meta?.cacheWriteTokens ?? 0,
        output: meta?.outputTokens ?? 0,
        netInput: netInput(meta ?? {}),
      };
    });

  const totalInput = overall?.inputTokens ?? 0;
  const totalCacheRead = overall?.cacheReadTokens ?? 0;
  const totalCacheWrite = overall?.cacheWriteTokens ?? 0;
  const totalOutput = overall?.outputTokens ?? 0;
  const totalNet = overall?.totalTokens ?? 0;
  const costUsd = overall ? calcCostUsd(overall, model.price) : 0;

  const enabledTools = Object.entries(tools)
    .filter(([, v]) => v)
    .map(([k]) => k)
    .join(", ") || "none";

  return [
    "# Token Usage Playground Report",
    "",
    `**Date:** ${new Date().toISOString()}`,
    `**Model:** ${model.label} (${model.id})`,
    `**Est. cost:** ${fmtUsd(costUsd)} / ${fmtNzd(costUsd)} (approx. NZD rate: ${USD_TO_NZD})`,
    `**Tools enabled:** ${enabledTools}`,
    `**KB files:** ${kbFileIds.length} file(s)`,
    `**Turns:** ${turns.length}`,
    "",
    "## Totals",
    `| Metric | Value |`,
    `|--------|-------|`,
    `| Total input tokens | ${totalInput.toLocaleString()} |`,
    `| Cache read (discounted) | ${totalCacheRead.toLocaleString()} |`,
    `| Cache write (extra) | ${totalCacheWrite.toLocaleString()} |`,
    `| Net non-cached input | ${(totalInput - totalCacheRead).toLocaleString()} |`,
    `| Output tokens | ${totalOutput.toLocaleString()} |`,
    `| Net billed tokens | ${totalNet.toLocaleString()} |`,
    `| Cache hit rate | ${pct(totalCacheRead, totalInput)} |`,
    `| Est. cost (USD) | ${fmtUsd(costUsd)} |`,
    `| Est. cost (NZD ~${USD_TO_NZD}x) | ${fmtNzd(costUsd)} |`,
    "",
    "## Per-turn breakdown",
    "| Turn | Raw Input | Cache Read | Cache Write | Net Input | Output |",
    "|------|-----------|------------|-------------|-----------|--------|",
    ...turns.map(
      (t) =>
        `| ${t.turn} | ${t.inputTokens.toLocaleString()} | ${t.cacheRead.toLocaleString()} | ${t.cacheWrite.toLocaleString()} | ${t.netInput.toLocaleString()} | ${t.output.toLocaleString()} |`
    ),
    "",
    "## Recommendations",
    totalCacheRead / Math.max(1, totalInput) < 0.2
      ? "- **Low cache hit rate** — enable prompt caching on the system prompt to reduce repeated input cost"
      : "- Cache hit rate is healthy",
    turns.length >= 2 &&
    turns[turns.length - 1].inputTokens > turns[0].inputTokens * 1.5
      ? "- **Rapid context growth** — consider rolling summarisation or trimming old turns"
      : "- Context growth is stable",
    totalOutput / Math.max(1, totalInput + totalOutput) > 0.35
      ? "- **High output ratio** — add a conciseness instruction or set `max_tokens` to cap responses"
      : "- Output ratio is reasonable",
    kbFileIds.length === 0 && tools.knowledgeBase === false
      ? "- No Knowledge Base files enabled — add the PDF to measure RAG token overhead"
      : "",
    "",
    "---",
    "_Generated by Woods AI Token Playground_",
  ]
    .filter((l) => l !== null)
    .join("\n");
}

// ─── Chart config ─────────────────────────────────────────────────────────────

const CHART_CONFIG = {
  netInput:   { label: "Net Input",   color: "var(--token-input)" },
  cacheRead:  { label: "Cache Read",  color: "var(--token-cache-read)" },
  cacheWrite: { label: "Cache Write", color: "var(--token-cache-write)" },
  output:     { label: "Output",      color: "var(--token-output)" },
} satisfies ChartConfig;

const COST_CHART_CONFIG = {
  costUsd: { label: "Cost (USD)", color: "hsl(var(--chart-5))" },
} satisfies ChartConfig;

// ─── Tool name helper ─────────────────────────────────────────────────────────

function toolLabel(part: { type: string; toolName?: string }): string {
  const raw = part.type === "dynamic-tool" && part.toolName
    ? part.toolName
    : part.type.replace(/^tool-/, "");
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Returns a short result-count/size annotation for a completed tool call. */
function toolResultAnnotation(part: { type: string; toolName?: string; output?: unknown; state?: string }): string | null {
  if (part.state !== "output-available" && part.state !== "result") return null;
  const toolName = part.type === "dynamic-tool" ? (part.toolName ?? "") : part.type.replace(/^tool-/, "");
  const output = part.output;
  if (!output) return null;

  // web_search: output is an array of result objects
  if (
    toolName === "web_search" ||
    toolName === "web_search_openai" ||
    toolName === "web_search_20250305" ||
    toolName === "web_search_20260209"
  ) {
    if (Array.isArray(output)) return `${output.length} result${output.length !== 1 ? "s" : ""}`;
    // Sometimes wrapped as { results: [] }
    const results = (output as Record<string, unknown>).results;
    if (Array.isArray(results)) return `${results.length} result${results.length !== 1 ? "s" : ""}`;
    // OpenAI shape: { action, sources: [{ type: "url", url }] }
    const sources = (output as Record<string, unknown>).sources;
    if (Array.isArray(sources)) return `${sources.length} result${sources.length !== 1 ? "s" : ""}`;
  }

  // web_fetch: output is a string (page text)
  if (toolName === "web_fetch" || toolName === "web_fetch_20260209") {
    const text = typeof output === "string" ? output : JSON.stringify(output);
    const chars = text.length;
    return chars > 1000 ? `${Math.round(chars / 1000)}k chars` : `${chars} chars`;
  }

  // knowledge_base: output has sources array
  if (toolName === "knowledge_base") {
    const sources = (output as Record<string, unknown>).sources;
    if (Array.isArray(sources)) return `${sources.length} chunk${sources.length !== 1 ? "s" : ""}`;
  }

  return null;
}

// ─── KB file type ─────────────────────────────────────────────────────────────

type KBFile = {
  id: string;
  name: string;
  processing_status: string | null;
};

// ─── Component ────────────────────────────────────────────────────────────────

export type PlaygroundPanelProps = {
  onBack?: () => void;
};

export function PlaygroundPanel({ onBack }: PlaygroundPanelProps) {
  const [tools, setTools] = useState<ToolFlags>(DEFAULT_TOOLS);
  const [codeExecutionEnabled, setCodeExecutionEnabled] = useState(false);
  const [kbFiles, setKbFiles] = useState<KBFile[]>([]);
  const [selectedKbIds, setSelectedKbIds] = useState<string[]>([]);
  const [manualFileIdInput, setManualFileIdInput] = useState("");
  const [input, setInput] = useState("");
  const [selectedModel, setSelectedModel] = useState<ModelOption>(DEFAULT_MODEL);
  const [selectedTurnIdx, setSelectedTurnIdx] = useState<number | null>(null);
  const [inspectedToolKey, setInspectedToolKey] = useState<string | null>(null);
  const [turnAnalysis, setTurnAnalysis] = useState<{
    systemTokens: number;
    toolDefTokens: number;
    userMsgTokens: number;
  } | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Refs for transport body closure
  const accessTokenRef = useRef<string | null>(null);
  const userIdRef = useRef<string | null>(null);
  const toolsRef = useRef<ToolFlags>(tools);
  const kbIdsRef = useRef<string[]>(selectedKbIds);
  const modelRef = useRef<ModelOption>(DEFAULT_MODEL);

  const effectiveTools = useMemo<ToolFlags>(
    () => ({
      ...tools,
      codeExecution: codeExecutionEnabled && tools.codeExecution,
    }),
    [tools, codeExecutionEnabled]
  );

  useEffect(() => { toolsRef.current = effectiveTools; }, [effectiveTools]);
  useEffect(() => { kbIdsRef.current = selectedKbIds; }, [selectedKbIds]);
  useEffect(() => { modelRef.current = selectedModel; }, [selectedModel]);

  // DB-driven code execution gate for playground.
  useEffect(() => {
    let cancelled = false;

    const loadCodeExecutionEnabled = async () => {
      const [toolRes, sessionRes] = await Promise.all([
        supabase
          .from("tools")
          .select("enabled")
          .eq("id", BUILTIN_TOOL_UUID.codeExecution)
          .maybeSingle(),
        supabase.auth.getSession(),
      ]);

      if (cancelled) return;

      if (toolRes.error) {
        console.error("[PlaygroundPanel] code execution gate fetch error:", toolRes.error.message);
        setCodeExecutionEnabled(false);
        return;
      }

      const userId = sessionRes.data.session?.user.id;
      if (!userId) {
        setCodeExecutionEnabled(false);
        return;
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("profile_tools")
        .eq("user_id", userId)
        .maybeSingle();

      if (cancelled) return;

      if (profileError) {
        console.error("[PlaygroundPanel] profile tools fetch error:", profileError.message);
        setCodeExecutionEnabled(false);
        return;
      }

      const profileTools =
        (profileData?.profile_tools as Array<{ tool_id?: string }> | null | undefined) ?? [];
      const hasCodeExecutionInProfile = profileTools.some(
        (entry) => entry.tool_id === BUILTIN_TOOL_UUID.codeExecution
      );

      const globallyEnabled = Boolean(toolRes.data?.enabled);
      setCodeExecutionEnabled(globallyEnabled && hasCodeExecutionInProfile);
    };

    void loadCodeExecutionEnabled();

    return () => {
      cancelled = true;
    };
  }, []);



  // Keep auth token fresh
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      accessTokenRef.current = data.session?.access_token ?? null;
      userIdRef.current = data.session?.user.id ?? null;
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => {
      accessTokenRef.current = s?.access_token ?? null;
      userIdRef.current = s?.user.id ?? null;
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch the user's completed KB files
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      const userId = data.session?.user.id;
      if (!userId) return;
      const { data: rows } = await supabase
        .from("files")
        .select("id, name, processing_status")
        .eq("user_id", userId)
        .eq("processing_status", "COMPLETED")
        .order("name");
      if (rows) setKbFiles(rows as KBFile[]);
    });
  }, []);

  // Build transport once. body/headers are callbacks that read the latest refs
  // at fetch time, so the transport itself never needs to be recreated.
  const transport = useMemo(
    () =>
      new DefaultChatTransport<ChatUIMessage>({
        api: "/api/chat",
        headers: () => {
          const token = accessTokenRef.current;
          return token
            ? ({ Authorization: `Bearer ${token}` } as Record<string, string>)
            : ({} as Record<string, string>);
        },
        body: () => ({
          model: modelRef.current.id,
          activeTools: buildActiveTools(toolsRef.current, kbIdsRef.current),
          userId: userIdRef.current,
          incognito: true,
          source: "playground",
        }),
      }),
    [],
  );

  const { messages, sendMessage, status, setMessages } = useChat<ChatUIMessage>({
    transport,
  });

  const isStreaming = status === "submitted" || status === "streaming";

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch token analysis when a turn is selected
  useEffect(() => {
    if (selectedTurnIdx === null) return;

    const assistantMsgs = messages.filter((m) => m.role === "assistant");
    const assistantMsg = assistantMsgs[selectedTurnIdx];
    if (!assistantMsg) return;

    // Find the user message immediately preceding this assistant turn
    const aIdx = messages.findIndex((m) => m.id === assistantMsg.id);
    const userMsg = aIdx > 0 ? messages.slice(0, aIdx).reverse().find((m) => m.role === "user") : undefined;
    const userText = userMsg?.parts
      .filter((p) => p.type === "text")
      .map((p) => (p as { type: "text"; text: string }).text)
      .join(" ") ?? "";

    let cancelled = false;

    const load = async () => {
      setAnalysisLoading(true);
      setTurnAnalysis(null);
      try {
        const r = await fetch("/api/playground/analyze", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            model: selectedModel.id,
            toolFlags: {
              webSearch: effectiveTools.webSearch,
              webFetch: effectiveTools.webFetch,
              codeExecution: effectiveTools.codeExecution,
              memory: effectiveTools.memory,
              knowledgeBase: effectiveTools.knowledgeBase,
            },
            userMessage: userText,
          }),
        });
        const data = await r.json();
        if (!cancelled) setTurnAnalysis(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setAnalysisLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [selectedTurnIdx, messages, selectedModel.id, effectiveTools]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    sendMessage({ text });
  }, [input, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleClear = useCallback(() => {
    setMessages([]);
  }, [setMessages]);

  // ─── Token aggregation ────────────────────────────────────────────────────

  const assistantMessages = useMemo(
    () => messages.filter((m) => m.role === "assistant"),
    [messages]
  );

  const overall = useMemo(
    () =>
      sumChatMessageMetadata(
        assistantMessages.map((m) => m.metadata as ChatMessageMetadata | undefined)
      ),
    [assistantMessages]
  );

  const chartData = useMemo(
    () =>
      assistantMessages.map((m, i) => {
        const meta = (m.metadata ?? {}) as ChatMessageMetadata;
        return {
          turn: `T${i + 1}`,
          netInput: netInput(meta),
          cacheRead: meta.cacheReadTokens ?? 0,
          cacheWrite: meta.cacheWriteTokens ?? 0,
          output: meta.outputTokens ?? 0,
        };
      }),
    [assistantMessages]
  );

  const costChartData = useMemo(
    () =>
      assistantMessages.map((m, i) => {
        const meta = (m.metadata ?? {}) as ChatMessageMetadata;
        return { turn: `T${i + 1}`, costUsd: calcCostUsd(meta, selectedModel.price) };
      }),
    [assistantMessages, selectedModel]
  );

  // Aggregate tool calls across all messages
  const toolCallData = useMemo(() => {
    const counts = new Map<string, { count: number; turns: number[] }>();
    messages.forEach((msg, _msgIdx) => {
      const turnIdx = assistantMessages.findIndex((am) => am.id === msg.id);
      msg.parts.forEach((part) => {
        if (part.type !== "dynamic-tool" && !part.type.startsWith("tool-")) return;
        const p = part as { type: string; toolName?: string; state?: string };
        if (p.state !== "output-available" && p.state !== "result") return;
        const label = toolLabel(p);
        const existing = counts.get(label) ?? { count: 0, turns: [] };
        existing.count += 1;
        if (turnIdx >= 0 && !existing.turns.includes(turnIdx + 1)) {
          existing.turns.push(turnIdx + 1);
        }
        counts.set(label, existing);
      });
    });
    return Array.from(counts.entries())
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.count - a.count);
  }, [messages, assistantMessages]);

  // Thinking / reasoning extraction
  const thinkingData = useMemo(
    () =>
      assistantMessages.map((m, i) => {
        const reasoningPart = m.parts.find((p) => p.type === "reasoning") as
          | { type: "reasoning"; text: string }
          | undefined;
        return {
          turn: i + 1,
          hasThinking: Boolean(reasoningPart),
          chars: reasoningPart?.text.length ?? 0,
          estTokens: Math.round((reasoningPart?.text.length ?? 0) / 4),
        };
      }),
    [assistantMessages]
  );
  const totalThinkingTokens = useMemo(
    () => thinkingData.reduce((s, d) => s + d.estTokens, 0),
    [thinkingData]
  );
  const turnsWithThinking = thinkingData.filter((d) => d.hasThinking).length;

  const totalInput = overall?.inputTokens ?? 0;
  const totalCacheRead = overall?.cacheReadTokens ?? 0;
  const totalCacheWrite = overall?.cacheWriteTokens ?? 0;
  const totalOutput = overall?.outputTokens ?? 0;
  const totalNet = overall?.totalTokens ?? 0;
  const cacheHitRate = pct(totalCacheRead, totalInput);

  const totalCostUsd = useMemo(
    () => overall ? calcCostUsd(overall, selectedModel.price) : 0,
    [overall, selectedModel]
  );

  // ─── Export report ────────────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    const report = buildReport(messages, overall, effectiveTools, selectedKbIds, selectedModel);
    navigator.clipboard.writeText(report).then(() => {
      toast.success("Report copied to clipboard");
    });
  }, [messages, overall, effectiveTools, selectedKbIds, selectedModel]);

  // ─── Toggle KB file ───────────────────────────────────────

  const toggleKbFile = useCallback((id: string) => {
    setSelectedKbIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setTools((prev) => ({ ...prev, knowledgeBase: true }));
  }, []);

  // ─── Add manual file ID ────────────────────────────────────

  const handleAddManualFileId = useCallback(() => {
    const id = manualFileIdInput.trim();
    if (!id) return;
    setSelectedKbIds((prev) => prev.includes(id) ? prev : [...prev, id]);
    setTools((prev) => ({ ...prev, knowledgeBase: true }));
    setManualFileIdInput("");
  }, [manualFileIdInput]);

  const removeFileId = useCallback((id: string) => {
    setSelectedKbIds((prev) => prev.filter((x) => x !== id));
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full w-full overflow-hidden bg-background">
      <PageHeader
        title="Token Playground"
        description="Debug token usage with all tools and knowledge base"
        onBack={onBack}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleClear} disabled={messages.length === 0}>
              Clear
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={assistantMessages.length === 0}>
              <CopyIcon className="size-3.5 mr-1.5" />
              Export Report
            </Button>
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left: Config + Chat ───────────────────────────────────────── */}
        <div className="flex flex-col w-[58%] min-w-0 border-r border-border overflow-hidden">
          {/* Toolbar: model + tool toggles */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 border-b border-border bg-muted/30 shrink-0">
            <Select
              value={selectedModel.id}
              onValueChange={(id) => {
                const m = MODEL_OPTIONS.find((o) => o.id === id) ?? DEFAULT_MODEL;
                setSelectedModel(m);
              }}
            >
              <SelectTrigger className="h-7 w-36 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MODEL_OPTIONS.map((m) => (
                  <SelectItem key={m.id} value={m.id} className="text-xs">
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="w-px h-4 bg-border shrink-0" />
            {(Object.keys(DEFAULT_TOOLS) as (keyof ToolFlags)[]).map((key) => (
              <div key={key} className="flex items-center gap-1.5">
                <Switch
                  id={`tool-${key}`}
                  checked={effectiveTools[key]}
                  disabled={key === "codeExecution" && !codeExecutionEnabled}
                  onCheckedChange={(v) => {
                    setTools((prev) => ({
                      ...prev,
                      [key]: key === "codeExecution" && !codeExecutionEnabled ? false : v,
                    }));
                  }}
                  className="scale-90"
                />
                <Label
                  htmlFor={`tool-${key}`}
                  className={[
                    "text-xs capitalize",
                    key === "codeExecution" && !codeExecutionEnabled
                      ? "cursor-not-allowed text-muted-foreground"
                      : "cursor-pointer",
                  ].join(" ")}
                >
                  {key === "webSearch" ? "Web Search"
                    : key === "webFetch" ? "Web Fetch"
                    : key === "codeExecution"
                      ? codeExecutionEnabled
                        ? "Code Exec"
                        : "Code Exec (DB off)"
                    : key === "knowledgeBase" ? "KB"
                    : "Memory"}
                </Label>
              </div>
            ))}
          </div>

          {/* KB file selector */}
          {tools.knowledgeBase && (
            <div className="flex flex-col gap-2 px-4 py-2 border-b border-border bg-muted/20 shrink-0">
              {/* Uploaded KB files */}
              {kbFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs text-muted-foreground self-center mr-1 w-full">KB files:</span>
                  {kbFiles.map((f) => (
                    <Badge
                      key={f.id}
                      variant={selectedKbIds.includes(f.id) ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => toggleKbFile(f.id)}
                    >
                      {f.name}
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Manual file ID input */}
              <div className="flex gap-1.5 items-end">
                <div className="flex-1">
                  <Label htmlFor="manual-file-id" className="text-[11px] text-muted-foreground block mb-1">
                    Add file ID
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      id="manual-file-id"
                      placeholder="Paste file UUID…"
                      value={manualFileIdInput}
                      onChange={(e) => setManualFileIdInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddManualFileId();
                        }
                      }}
                      className="h-7 text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={handleAddManualFileId}
                      disabled={!manualFileIdInput.trim()}
                      className="h-7 px-2 text-xs shrink-0"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Selected file IDs (both uploaded and manual) */}
              {selectedKbIds.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1 border-t border-border/50">
                  <span className="text-xs text-muted-foreground self-center mr-1 w-full">Selected:</span>
                  {selectedKbIds.map((id) => {
                    const uploadedFile = kbFiles.find((f) => f.id === id);
                    const displayName = uploadedFile?.name || id.slice(0, 8) + "…";
                    return (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="text-xs pr-1 pl-2 h-6"
                      >
                        <span className="truncate max-w-[150px]" title={displayName}>
                          {displayName}
                        </span>
                        <button
                          onClick={() => removeFileId(id)}
                          className="ml-1 hover:text-destructive inline-flex shrink-0"
                          title="Remove"
                        >
                          <XIcon className="size-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="flex flex-col gap-3 p-4">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                  <p className="text-sm">Send a message to start debugging token usage.</p>
                  <p className="text-xs mt-1">All tools are pre-enabled. Select KB files above to test RAG overhead.</p>
                </div>
              )}
              {messages.map((msg) => {
                const meta = msg.role === "assistant"
                  ? (msg.metadata as ChatMessageMetadata | undefined)
                  : undefined;
                const reasoningPart = msg.parts.find((p) => p.type === "reasoning") as
                  | { type: "reasoning"; text: string } | undefined;
                const toolParts = msg.parts.filter(
                  (p) => p.type === "dynamic-tool" || p.type.startsWith("tool-")
                ) as { type: string; toolName?: string; state?: string; input?: unknown; output?: unknown }[];
                const textParts = msg.parts.filter((p) => p.type === "text");
                return (
                  <div
                    key={msg.id}
                    className={msg.role === "user" ? "flex justify-end" : "flex justify-start"}
                  >
                    <div
                      className={
                        msg.role === "user"
                          ? "max-w-[80%] rounded-2xl bg-primary text-primary-foreground px-3.5 py-2.5 text-sm"
                          : "max-w-[90%] w-full flex flex-col gap-1.5"
                      }
                    >
                      {/* Reasoning block */}
                      {reasoningPart && (
                        <Reasoning isStreaming={isStreaming && msg === messages[messages.length - 1]}>
                          <ReasoningTrigger className="text-xs" />
                          <ReasoningContent>{reasoningPart.text}</ReasoningContent>
                        </Reasoning>
                      )}
                      {/* Tool call chips */}
                      {toolParts.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {toolParts.map((tp, i) => {
                            const done = tp.state === "output-available" || tp.state === "result";
                            const annotation = done ? toolResultAnnotation(tp as { type: string; toolName?: string; output?: unknown; state?: string }) : null;
                            const toolKey = `${msg.id}-${i}`;
                            const isOpen = inspectedToolKey === toolKey;
                            const out = tp.output as Record<string, unknown>;
                            return (
                              <>
                                <Badge 
                                  key={i}
                                  variant="outline" 
                                  className="text-[10px] h-5 px-1.5 gap-1 cursor-pointer hover:bg-muted"
                                  onClick={() => setInspectedToolKey(isOpen ? null : toolKey)}
                                >
                                  <WrenchIcon className="size-2.5" />
                                  {toolLabel(tp)}
                                  {done
                                    ? annotation
                                      ? ` · ${annotation}`
                                      : " ✓"
                                    : " …"}
                                </Badge>
                                <Dialog open={isOpen} onOpenChange={(open) => setInspectedToolKey(open ? toolKey : null)}>
                                  <DialogContent className="w-[80vw] max-h-[90vh] overflow-hidden flex flex-col">
                                    <DialogHeader className="border-b border-border pb-4 shrink-0">
                                      <DialogTitle className="text-lg">
                                        {toolLabel(tp)}
                                        <p className="text-xs text-muted-foreground font-normal mt-1">
                                          State: <span className="font-mono font-medium">{tp.state || "pending"}</span>
                                        </p>
                                      </DialogTitle>
                                    </DialogHeader>
                                    <div className="flex gap-4 flex-1 overflow-hidden">
                                      {/* Left column: Input */}
                                      <div className="flex-1 overflow-y-auto pr-4">
                                        {tp.input !== undefined && (
                                          <div>
                                            <p className="font-semibold text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">Input</p>
                                            <pre className="bg-muted/50 border border-border p-3 rounded text-xs overflow-auto max-h-[calc(90vh-200px)] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                              {typeof tp.input === "string"
                                                ? tp.input
                                                : JSON.stringify(tp.input, null, 2)}
                                            </pre>
                                          </div>
                                        )}
                                        {tp.input === undefined && (
                                          <p className="text-xs text-muted-foreground italic">No input available</p>
                                        )}
                                      </div>

                                      {/* Right column: Output */}
                                      <div className="flex-1 overflow-y-auto">
                                        {tp.output !== undefined && (
                                          <div>
                                            <p className="font-semibold text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">Output</p>
                                            <div className="space-y-2">
                                              {typeof tp.output === "string" ? (
                                                <pre className="bg-muted/50 border border-border p-3 rounded text-xs overflow-auto max-h-[calc(90vh-200px)] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                  {tp.output as string}
                                                </pre>
                                              ) : (
                                                <div className="space-y-2">
                                                  {(out.stdout || out.stderr || out.return_code !== undefined) ? (
                                                    <>
                                                      {out.stdout && (
                                                    <div>
                                                      <p className="text-[10px] font-medium text-muted-foreground mb-1">stdout:</p>
                                                      <pre className="bg-muted/50 border border-border p-2.5 rounded text-xs overflow-auto max-h-[160px] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                        {out.stdout as string}
                                                      </pre>
                                                    </div>
                                                  )}
                                                  {out.stderr && (
                                                    <div>
                                                      <p className="text-[10px] font-medium text-destructive mb-1">stderr:</p>
                                                      <pre className="bg-destructive/5 border border-destructive/20 p-2.5 rounded text-xs overflow-auto max-h-[160px] font-mono break-words whitespace-pre-wrap leading-relaxed text-destructive">
                                                        {out.stderr as string}
                                                      </pre>
                                                    </div>
                                                  )}
                                                  {out.return_code !== undefined && (
                                                    <p className="text-[10px] font-mono">
                                                      Return code: <span className={(out.return_code as number) === 0 ? "text-green-600" : "text-destructive"}>{out.return_code as number}</span>
                                                    </p>
                                                  )}
                                                  {out.sources && (
                                                    <div>
                                                      <p className="text-[10px] font-medium text-muted-foreground mb-1">
                                                        Sources ({(out.sources as unknown[]).length}):
                                                      </p>
                                                      <div className="space-y-1">
                                                        {(out.sources as Array<{rank?: number; id?: string; similarity?: number; content?: string}>).map((source, idx: number) => (
                                                          <div key={idx} className="text-[10px] bg-muted p-2 rounded border border-border/50">
                                                            <p className="font-mono font-medium text-primary mb-0.5">{source.rank || idx + 1}. {source.id?.slice(0, 8)}...</p>
                                                            {source.similarity !== undefined && (
                                                              <p className="text-muted-foreground mb-1">Similarity: {(source.similarity * 100).toFixed(1)}%</p>
                                                            )}
                                                            {source.content && (
                                                              <p className="text-muted-foreground line-clamp-3 text-xs">{source.content}</p>
                                                            )}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {out.result && (
                                                    <div>
                                                      <p className="text-[10px] font-medium text-muted-foreground mb-1">Result:</p>
                                                      <pre className="bg-muted/50 border border-border p-2.5 rounded text-xs overflow-auto max-h-[160px] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                        {typeof out.result === "string"
                                                          ? out.result
                                                          : JSON.stringify(out.result, null, 2)}
                                                      </pre>
                                                    </div>
                                                  )}
                                                  {out.content && Array.isArray(out.content) && out.content.length > 0 && (
                                                    <div>
                                                      <p className="text-[10px] font-medium text-muted-foreground mb-1">Content:</p>
                                                      <pre className="bg-muted/50 border border-border p-2.5 rounded text-xs overflow-auto max-h-[160px] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                        {JSON.stringify(out.content, null, 2)}
                                                      </pre>
                                                    </div>
                                                  )}
                                                  {/* Other fields */}
                                                  {(() => {
                                                    const shown = new Set(["stdout", "stderr", "return_code", "sources", "result", "content", "type"]);
                                                    const other = Object.entries(out).filter(
                                                      ([key]) => !shown.has(key)
                                                    );
                                                    return other.length > 0 ? (
                                                      <div className="space-y-1">
                                                        {other.map(([key, value]: [string, unknown]) => (
                                                          <div key={key}>
                                                            <p className="text-[10px] font-medium text-muted-foreground mb-1 capitalize">{key}:</p>
                                                            <pre className="bg-muted/50 border border-border p-2.5 rounded text-xs overflow-auto max-h-[160px] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                              {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
                                                            </pre>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                  {/* Fallback for other structured output */}
                                                  {!out.stdout && !out.stderr && !out.sources && (
                                                    <pre className="bg-muted/50 border border-border p-3 rounded text-xs overflow-auto max-h-[200px] font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                      {JSON.stringify(out, null, 2)}
                                                    </pre>
                                                  )}
                                                    </>
                                                  ) : (
                                                    <p className="text-[10px] text-muted-foreground italic">No output available</p>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {tp.output === undefined && (
                                          <p className="text-xs text-muted-foreground italic">No output available yet</p>
                                        )}
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </>
                            );
                          })}
                        </div>
                      )}
                      {/* Text */}
                      {textParts.map((p, i) =>
                        p.type === "text" ? (
                          <MessageResponse
                            key={i}
                            isAnimating={isStreaming}
                            disallowedElements={["script", "style"]}
                          >
                            {p.text}
                          </MessageResponse>
                        ) : null
                      )}
                      {/* Token badges */}
                      {meta && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {meta.inputTokens !== undefined && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono">
                              in {meta.inputTokens.toLocaleString()}
                            </Badge>
                          )}
                          {meta.cacheReadTokens !== undefined && meta.cacheReadTokens > 0 && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono text-chart-2">
                              cache↓ {meta.cacheReadTokens.toLocaleString()}
                            </Badge>
                          )}
                          {meta.cacheWriteTokens !== undefined && meta.cacheWriteTokens > 0 && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono text-chart-3">
                              cache↑ {meta.cacheWriteTokens.toLocaleString()}
                            </Badge>
                          )}
                          {meta.outputTokens !== undefined && (
                            <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-mono text-chart-4">
                              out {meta.outputTokens.toLocaleString()}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="shrink-0 border-t border-border p-3">
            <div className="flex gap-2 items-end">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Send a message… (Enter to send, Shift+Enter for new line)"
                rows={2}
                className="resize-none text-sm"
                disabled={isStreaming}
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!input.trim() || isStreaming}
                className="shrink-0 size-9"
              >
                {isStreaming ? (
                  <SquareIcon className="size-4" />
                ) : (
                  <SendIcon className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Right: Token Dashboard ─────────────────────────────────────── */}
        <ScrollArea className="flex-1 min-w-0">
          <div className="p-4 flex flex-col gap-4">
            {/* Session metrics — compact strip */}
            <Card>
              <CardHeader className="pb-1 pt-2.5 px-3">
                <CardTitle className="text-[11px] text-muted-foreground font-normal uppercase tracking-wide">
                  Session total
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 pb-2.5">
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  {[
                    { label: "Cost (USD)",    value: totalCostUsd > 0 ? fmtUsd(totalCostUsd) : "—" },
                    { label: "Cost (NZD)",    value: totalCostUsd > 0 ? fmtNzd(totalCostUsd) : "—" },
                    { label: "Cache hit",     value: totalInput > 0 ? cacheHitRate : "—" },
                    { label: "Net tokens",    value: totalNet > 0 ? totalNet.toLocaleString() : "—" },
                    { label: "Cache read",    value: totalCacheRead > 0 ? totalCacheRead.toLocaleString() : "—" },
                    { label: "Cache write",   value: totalCacheWrite > 0 ? totalCacheWrite.toLocaleString() : "—" },
                    { label: "Input tokens",  value: totalInput > 0 ? totalInput.toLocaleString() : "—" },
                    { label: "Output tokens", value: totalOutput > 0 ? totalOutput.toLocaleString() : "—" },
                    { label: "Turns",         value: assistantMessages.length > 0 ? String(assistantMessages.length) : "—" },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-[10px] text-muted-foreground leading-none">{label}</p>
                      <p className="text-sm font-semibold tabular-nums mt-0.5 leading-none">{value}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Per-turn chart */}
            {chartData.length > 0 && (() => {
              const sel = selectedTurnIdx !== null ? assistantMessages[selectedTurnIdx] : null;
              const selMeta = sel ? (sel.metadata ?? {}) as ChatMessageMetadata : null;
              const selTools = sel
                ? sel.parts.filter((p) => p.type === "dynamic-tool" || p.type.startsWith("tool-")) as { type: string; toolName?: string; state?: string }[]
                : [];
              const selThinking = sel
                ? sel.parts.find((p) => p.type === "reasoning") as { type: "reasoning"; text: string } | undefined
                : undefined;
              const selCost = selMeta ? calcCostUsd(selMeta, selectedModel.price) : 0;

              return (
                <Card>
                  <CardHeader className="pb-2 pt-3 px-3">
                    <CardTitle className="text-xs font-medium">
                      Token breakdown by turn
                      <span className="ml-1.5 font-normal text-muted-foreground">(click a bar to inspect)</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <ChartContainer config={CHART_CONFIG} className="h-48 w-full">
                      <BarChart
                        data={chartData}
                        margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
                        style={{ cursor: "pointer" }}
                        onClick={(d) => {
                          if (!d?.activeLabel) return;
                          const idx = parseInt(String(d.activeLabel).replace("T", ""), 10) - 1;
                          setSelectedTurnIdx((prev) => prev === idx ? null : idx);
                        }}
                      >
                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                        <XAxis dataKey="turn" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar dataKey="netInput"   stackId="a" fill="var(--color-netInput)"   radius={[0, 0, 0, 0]}
                          opacity={selectedTurnIdx === null ? 1 : chartData.map((_, i) => i === selectedTurnIdx ? 1 : 0.35).join(",")} />
                        <Bar dataKey="cacheRead"  stackId="a" fill="var(--color-cacheRead)"  radius={[0, 0, 0, 0]} />
                        <Bar dataKey="cacheWrite" stackId="a" fill="var(--color-cacheWrite)" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="output"     stackId="a" fill="var(--color-output)"     radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ChartContainer>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-3 mt-2">
                      {Object.entries(CHART_CONFIG).map(([key, cfg]) => (
                        <div key={key} className="flex items-center gap-1.5">
                          <span className="inline-block size-2.5 rounded-sm" style={{ background: cfg.color }} />
                          <span className="text-[11px] text-muted-foreground">{cfg.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Turn detail */}
                    {sel && selMeta && (() => {
                      const rawInput    = selMeta.inputTokens      ?? 0;
                      const cacheRead   = selMeta.cacheReadTokens  ?? 0;
                      const cacheWrite  = selMeta.cacheWriteTokens ?? 0;
                      const output      = selMeta.outputTokens     ?? 0;
                      const sysT        = turnAnalysis?.systemTokens  ?? null;
                      const toolDefT    = turnAnalysis?.toolDefTokens  ?? null;
                      const userMsgT    = turnAnalysis?.userMsgTokens  ?? null;
                      // history = everything that isn't system, tool defs, or user msg
                      const historyT    = (sysT !== null && toolDefT !== null && userMsgT !== null)
                        ? Math.max(0, rawInput - sysT - toolDefT - userMsgT - cacheRead)
                        : null;
                      const toolOutputEstT = selTools.reduce((s, tp) => {
                        const out = (tp as { output?: unknown }).output;
                        return s + Math.round(JSON.stringify(out ?? "").length / 4);
                      }, 0);

                      return (
                        <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 flex flex-col gap-3">
                          <p className="text-xs font-medium">Turn {selectedTurnIdx! + 1} — token breakdown</p>

                          {/* Input composition */}
                          <div>
                            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Input ({rawInput.toLocaleString()} tokens)</p>
                            <table className="w-full text-xs">
                              <tbody className="divide-y divide-border">
                                {sysT !== null && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">System instructions</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">{sysT.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(sysT, rawInput)}</td>
                                  </tr>
                                )}
                                {toolDefT !== null && toolDefT > 0 && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">
                                      KB tool definition
                                      <span className="ml-1 text-[10px] opacity-60">(custom only)</span>
                                    </td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">{toolDefT.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(toolDefT, rawInput)}</td>
                                  </tr>
                                )}
                                {userMsgT !== null && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">User prompt</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">{userMsgT.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(userMsgT, rawInput)}</td>
                                  </tr>
                                )}
                                {historyT !== null && historyT > 0 && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">Conversation history</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">{historyT.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(historyT, rawInput)}</td>
                                  </tr>
                                )}
                                {cacheRead > 0 && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">Cache read (−90% cost)</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">−{cacheRead.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(cacheRead, rawInput)}</td>
                                  </tr>
                                )}
                                {cacheWrite > 0 && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">Cache write (+25% cost)</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">{cacheWrite.toLocaleString()}</td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">{pct(cacheWrite, rawInput)}</td>
                                  </tr>
                                )}
                                {analysisLoading && (
                                  <tr>
                                    <td colSpan={3} className="py-0.5 text-muted-foreground italic">Counting tokens…</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                            {(effectiveTools.webSearch || effectiveTools.webFetch || effectiveTools.codeExecution || effectiveTools.memory) && (
                              <p className="text-[10px] text-muted-foreground mt-1.5 opacity-70">
                                Built-in tool overhead (web search, fetch, code exec, memory) is bundled into the API-reported input tokens and cannot be isolated separately.
                              </p>
                            )}
                          </div>

                          {/* Output */}
                          <div>
                            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Output ({output.toLocaleString()} tokens)</p>
                            <table className="w-full text-xs">
                              <tbody className="divide-y divide-border">
                                {selThinking && (
                                  <tr>
                                    <td className="py-0.5 pl-2 text-muted-foreground">Thinking (est.)</td>
                                    <td className="py-0.5 text-right tabular-nums font-medium">
                                      ~{Math.round(selThinking.text.length / 4).toLocaleString()}
                                    </td>
                                    <td className="py-0.5 text-right text-muted-foreground pl-2">
                                      {pct(Math.round(selThinking.text.length / 4), output)}
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <td className="py-0.5 pl-2 text-muted-foreground">Response text</td>
                                  <td className="py-0.5 text-right tabular-nums font-medium">
                                    {selThinking
                                      ? Math.max(0, output - Math.round(selThinking.text.length / 4)).toLocaleString()
                                      : output.toLocaleString()}
                                  </td>
                                  <td className="py-0.5 text-right text-muted-foreground pl-2">
                                    {selThinking
                                      ? pct(Math.max(0, output - Math.round(selThinking.text.length / 4)), output)
                                      : "100%"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Tools called this turn */}
                          {selTools.length > 0 && (
                            <div>
                              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">
                                Tools called ({selTools.length})
                              </p>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="pb-0.5 text-left font-normal pl-2">Tool</th>
                                    <th className="pb-0.5 text-right font-normal">Results</th>
                                    <th className="pb-0.5 text-right font-normal pl-2">Est. tok</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                  {selTools.map((tp, i) => {
                                    const out = (tp as { output?: unknown }).output;
                                    const estTok = Math.round(JSON.stringify(out ?? "").length / 4);
                                    const annotation = toolResultAnnotation(tp as { type: string; toolName?: string; output?: unknown; state?: string });
                                    return (
                                      <tr key={i}>
                                        <td className="py-0.5 pl-2 font-medium">{toolLabel(tp)}</td>
                                        <td className="py-0.5 text-right tabular-nums text-muted-foreground">
                                          {annotation ?? "—"}
                                        </td>
                                        <td className="py-0.5 text-right tabular-nums text-muted-foreground pl-2">
                                          ~{estTok.toLocaleString()}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {selTools.length > 1 && (
                                    <tr>
                                      <td className="py-0.5 pl-2 text-muted-foreground" colSpan={2}>Total output</td>
                                      <td className="py-0.5 text-right tabular-nums font-medium pl-2">
                                        ~{toolOutputEstT.toLocaleString()}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Cost */}
                          <div className="flex justify-between text-xs pt-1 border-t border-border">
                            <span className="text-muted-foreground">Est. cost</span>
                            <span className="font-medium tabular-nums">
                              {fmtUsd(selCost)} / {fmtNzd(selCost)}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Thinking card */}
            {assistantMessages.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <CardTitle className="text-xs font-medium flex items-center gap-1.5">
                    <BrainIcon className="size-3.5" />
                    Thinking (Chain of Thought)
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  {turnsWithThinking === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No extended thinking detected. Sonnet/Opus use adaptive thinking — it activates on complex prompts.
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-border">
                        <tr>
                          <td className="py-1 text-muted-foreground">Turns with thinking</td>
                          <td className="py-1 text-right font-medium tabular-nums">
                            {turnsWithThinking} / {assistantMessages.length}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-1 text-muted-foreground">Est. reasoning tokens</td>
                          <td className="py-1 text-right font-medium tabular-nums">
                            ~{totalThinkingTokens.toLocaleString()}
                          </td>
                        </tr>
                        {thinkingData.filter((d) => d.hasThinking).map((d) => (
                          <tr key={d.turn}>
                            <td className="py-1 text-muted-foreground pl-3">Turn {d.turn}</td>
                            <td className="py-1 text-right tabular-nums text-muted-foreground">
                              ~{d.estTokens.toLocaleString()} tok ({d.chars.toLocaleString()} chars)
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tool usage card */}
            {toolCallData.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <CardTitle className="text-xs font-medium flex items-center gap-1.5">
                    <WrenchIcon className="size-3.5" />
                    Tool Usage
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="pb-1 text-left font-normal">Tool</th>
                        <th className="pb-1 text-right font-normal">Calls</th>
                        <th className="pb-1 text-right font-normal">Turns</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {toolCallData.map((t) => (
                        <tr key={t.name}>
                          <td className="py-1 font-medium">{t.name}</td>
                          <td className="py-1 text-right tabular-nums">{t.count}</td>
                          <td className="py-1 text-right text-muted-foreground">
                            {t.turns.map((n) => `T${n}`).join(", ")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Cost per turn chart */}
            {costChartData.length > 0 && (
              <Card>
                <CardHeader className="pb-2 pt-3 px-3">
                  <CardTitle className="text-xs font-medium">Cost per turn (USD)</CardTitle>
                </CardHeader>
                <CardContent className="px-3 pb-3">
                  <ChartContainer config={COST_CHART_CONFIG} className="h-32 w-full">
                    <BarChart data={costChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" />
                      <XAxis dataKey="turn" tick={{ fontSize: 11 }} />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: number) =>
                          v < 0.001 ? `${(v * 1000).toFixed(2)}m` : `$${v.toFixed(4)}`
                        }
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(v) => [`$${Number(v).toFixed(6)}`, "Cost"]}
                          />
                        }
                      />
                      <Bar dataKey="costUsd" fill="var(--color-costUsd)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                </CardContent>
              </Card>
            )}

            {assistantMessages.length === 0 && (
              <div className="text-center text-xs text-muted-foreground py-8">
                Token metrics appear here after the first response.
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
